import Big from 'big.js';

/** 全局除法默认小数位精度。 */
Big.DP = 15;

/** 测量结果展示小数位（毫米 / 平方毫米）。 */
export const MEASURE_RESULT_DECIMAL_PLACES = 4;

/** 米到毫米的换算系数（字符串字面量，避免浮点污染）。 */
const METER_TO_MILLIMETER = '1000';

/** 平方米到平方毫米的换算系数。 */
const SQUARE_METER_TO_SQUARE_MILLIMETER = '1000000';

/** 直径系数。 */
const DIAMETER_FACTOR = '2';

/** 三角形面积系数（叉积长度 × 0.5）。 */
const TRIANGLE_AREA_HALF_FACTOR = '0.5';

/** 圆半径平方根前的校验阈值（米）。 */
const NON_POSITIVE_METERS_EPSILON = '1e-15';

/**
 * 将场景米制标量转为 Big.js 数值。
 *
 * 设计说明：场景坐标来自 IEEE754，只能以当前浮点字符串快照进入 Big.js，
 * 后续运算全程在 Big 域内完成，避免中途回到 number 累加。
 *
 * @param meters 场景米制标量。
 * @returns Big.js 数值。
 */
export function toBigFromMeterScalar(meters: number): Big {
    if (!Number.isFinite(meters)) {
        return new Big('0');
    }
    return new Big(meters.toString());
}

/**
 * 从十进制字符串创建 Big.js 数值。
 *
 * @param value 十进制字符串。
 * @returns Big.js 数值。
 */
export function toBigFromDecimalString(value: string): Big {
    return new Big(value);
}

/**
 * 将米制长度换算为毫米并圆整到展示精度。
 *
 * @param meters 米制长度。
 * @returns 毫米长度。
 */
export function meterLengthToMillimeter(meters: number): number {
    return roundMeasureScalarMillimeter(
        toBigFromMeterScalar(meters).times(METER_TO_MILLIMETER),
    );
}

/**
 * 将平方米面积换算为平方毫米并圆整到展示精度。
 *
 * @param squareMeters 平方米面积。
 * @returns 平方毫米面积。
 */
export function meterAreaToSquareMillimeter(squareMeters: number): number {
    return roundMeasureScalarMillimeter(
        toBigFromMeterScalar(squareMeters).times(SQUARE_METER_TO_SQUARE_MILLIMETER),
    );
}

/**
 * 计算两点分量差（毫米，有符号）。
 *
 * @param toMeters 目标点分量（米）。
 * @param fromMeters 基准点分量（米）。
 * @returns 分量差（毫米）。
 */
export function computeDeltaMillimeter(toMeters: number, fromMeters: number): number {
    return roundMeasureScalarMillimeter(
        toBigFromMeterScalar(toMeters)
            .minus(toBigFromMeterScalar(fromMeters))
            .times(METER_TO_MILLIMETER),
    );
}

/**
 * 计算三维欧氏距离（毫米）。
 *
 * @param ax 点 A X（米）。
 * @param ay 点 A Y（米）。
 * @param az 点 A Z（米）。
 * @param bx 点 B X（米）。
 * @param by 点 B Y（米）。
 * @param bz 点 B Z（米）。
 * @returns 距离（毫米）。
 */
export function computeDistance3DMillimeter(
    ax: number,
    ay: number,
    az: number,
    bx: number,
    by: number,
    bz: number,
): number {
    const dx = toBigFromMeterScalar(bx).minus(toBigFromMeterScalar(ax));
    const dy = toBigFromMeterScalar(by).minus(toBigFromMeterScalar(ay));
    const dz = toBigFromMeterScalar(bz).minus(toBigFromMeterScalar(az));
    const distanceMeters = dx.times(dx).plus(dy.times(dy)).plus(dz.times(dz)).sqrt();
    return meterLengthToMillimeter(distanceMeters.toNumber());
}

/**
 * 基于两个 THREE 兼容坐标点计算距离（毫米）。
 *
 * @param from 起点坐标（米）。
 * @param to 终点坐标（米）。
 * @returns 距离（毫米）。
 */
export function computeDistanceBetweenPointsMillimeter(
    from: { x: number; y: number; z: number },
    to: { x: number; y: number; z: number },
): number {
    return computeDistance3DMillimeter(from.x, from.y, from.z, to.x, to.y, to.z);
}

/**
 * 计算三维欧氏距离（米，仅供几何比较，不圆整）。
 *
 * @param ax 点 A X（米）。
 * @param ay 点 A Y（米）。
 * @param az 点 A Z（米）。
 * @param bx 点 B X（米）。
 * @param by 点 B Y（米）。
 * @param bz 点 B Z（米）。
 * @returns 距离（米）。
 */
export function computeDistance3DMeters(
    ax: number,
    ay: number,
    az: number,
    bx: number,
    by: number,
    bz: number,
): number {
    const dx = toBigFromMeterScalar(bx).minus(toBigFromMeterScalar(ax));
    const dy = toBigFromMeterScalar(by).minus(toBigFromMeterScalar(ay));
    const dz = toBigFromMeterScalar(bz).minus(toBigFromMeterScalar(az));
    return dx.times(dx).plus(dy.times(dy)).plus(dz.times(dz)).sqrt().toNumber();
}

/**
 * 将多段米制长度累加后换算为毫米。
 *
 * @param segmentLengthsMeters 各段长度（米）。
 * @returns 总长度（毫米）。
 */
export function sumMeterLengthsToMillimeter(segmentLengthsMeters: readonly number[]): number {
    let totalMeters = new Big('0');
    segmentLengthsMeters.forEach((segmentLength) => {
        if (!Number.isFinite(segmentLength)) {
            return;
        }
        totalMeters = totalMeters.plus(toBigFromMeterScalar(segmentLength));
    });
    return meterLengthToMillimeter(totalMeters.toNumber());
}

/**
 * 将多段米制长度在 Big 域内累加。
 *
 * @param segmentLengthsMeters 各段长度（米）。
 * @returns 总长度（米）。
 */
export function sumMeterLengths(segmentLengthsMeters: readonly number[]): number {
    let totalMeters = new Big('0');
    segmentLengthsMeters.forEach((segmentLength) => {
        if (!Number.isFinite(segmentLength)) {
            return;
        }
        totalMeters = totalMeters.plus(toBigFromMeterScalar(segmentLength));
    });
    return totalMeters.toNumber();
}

/**
 * 计算三角形叉积长度的一半（米），用于面积累加。
 *
 * @param edgeAx 边 A X 分量（米）。
 * @param edgeAy 边 A Y 分量（米）。
 * @param edgeAz 边 A Z 分量（米）。
 * @param edgeBx 边 B X 分量（米）。
 * @param edgeBy 边 B Y 分量（米）。
 * @param edgeBz 边 B Z 分量（米）。
 * @returns 三角形面积（平方米）。
 */
export function computeTriangleAreaMeters(
    edgeAx: number,
    edgeAy: number,
    edgeAz: number,
    edgeBx: number,
    edgeBy: number,
    edgeBz: number,
): number {
    const crossX = toBigFromMeterScalar(edgeAy).times(toBigFromMeterScalar(edgeBz))
        .minus(toBigFromMeterScalar(edgeAz).times(toBigFromMeterScalar(edgeBy)));
    const crossY = toBigFromMeterScalar(edgeAz).times(toBigFromMeterScalar(edgeBx))
        .minus(toBigFromMeterScalar(edgeAx).times(toBigFromMeterScalar(edgeBz)));
    const crossZ = toBigFromMeterScalar(edgeAx).times(toBigFromMeterScalar(edgeBy))
        .minus(toBigFromMeterScalar(edgeAy).times(toBigFromMeterScalar(edgeBx)));
    const crossLength = crossX.times(crossX)
        .plus(crossY.times(crossY))
        .plus(crossZ.times(crossZ))
        .sqrt();
    return crossLength.times(TRIANGLE_AREA_HALF_FACTOR).toNumber();
}

/**
 * 将米制半径换算为毫米。
 *
 * @param radiusMeters 半径（米）。
 * @returns 半径（毫米）；无效时返回 null。
 */
export function meterRadiusToMillimeter(radiusMeters: number): number | null {
    if (!Number.isFinite(radiusMeters) || radiusMeters <= Number.EPSILON) {
        return null;
    }
    return meterLengthToMillimeter(radiusMeters);
}

/**
 * 将毫米半径换算为直径（毫米）。
 *
 * @param radiusMillimeter 半径（毫米）。
 * @returns 直径（毫米）。
 */
export function millimeterRadiusToDiameter(radiusMillimeter: number): number {
    return roundMeasureScalarMillimeter(
        toBigFromMeterScalar(radiusMillimeter).times(DIAMETER_FACTOR),
    );
}

/**
 * 将米制半径换算为直径（毫米）。
 *
 * @param radiusMeters 半径（米）。
 * @returns 直径（毫米）；无效时返回 null。
 */
export function meterRadiusToDiameterMillimeter(radiusMeters: number): number | null {
    const radiusMillimeter = meterRadiusToMillimeter(radiusMeters);
    if (radiusMillimeter === null) {
        return null;
    }
    return millimeterRadiusToDiameter(radiusMillimeter);
}

/**
 * 将米制坐标元组换算为毫米坐标元组。
 *
 * @param tuple 坐标元组（米）。
 * @returns 坐标元组（毫米）。
 */
export function meterCoordinateTupleToMillimeterTuple(
    tuple: readonly [number, number, number],
): [number, number, number] {
    return [
        meterLengthToMillimeter(tuple[0]),
        meterLengthToMillimeter(tuple[1]),
        meterLengthToMillimeter(tuple[2]),
    ];
}

/**
 * 圆整测量标量到展示精度（毫米 / 平方毫米）。
 *
 * @param value 原始标量。
 * @returns 圆整后的标量。
 */
export function roundMeasureScalarMillimeter(value: Big | number): number {
    const normalized = value instanceof Big ? value : new Big(value.toString());
    const factor = new Big('10').pow(MEASURE_RESULT_DECIMAL_PLACES);
    return normalized.times(factor).round().div(factor).toNumber();
}

/**
 * 格式化测量数值为展示文本（毫米 / 平方毫米）。
 *
 * @param valueInMillimeter 数值（毫米或平方毫米）。
 * @returns 文本。
 */
export function formatMeasureNumber(valueInMillimeter: number): string {
    return roundMeasureScalarMillimeter(valueInMillimeter)
        .toFixed(MEASURE_RESULT_DECIMAL_PLACES);
}

/**
 * 格式化坐标元组为展示文本（毫米）。
 *
 * @param tuple 坐标元组（毫米）。
 * @returns 文本。
 */
export function formatMeasurePointTuple(tuple: readonly [number, number, number]): string {
    return `(${formatMeasureNumber(tuple[0])}, ${formatMeasureNumber(tuple[1])}, ${formatMeasureNumber(tuple[2])})`;
}

/** 长度测量展示单位后缀（含前导空格）。 */
const MEASURE_LENGTH_UNIT_SUFFIX = ' mm';

/** 面积测量展示单位后缀（含前导空格）。 */
const MEASURE_AREA_UNIT_SUFFIX = ' mm^2';

/**
 * 格式化长度测量值为带单位的展示文本（单选浮窗专用）。
 *
 * @param valueInMillimeter 长度（毫米）。
 * @returns 文本，例如 `12.3456 mm`。
 */
export function formatMeasureLengthWithUnit(valueInMillimeter: number): string {
    return `${formatMeasureNumber(valueInMillimeter)}${MEASURE_LENGTH_UNIT_SUFFIX}`;
}

/**
 * 格式化双选测量长度展示值（取绝对值并附带 mm 单位）。
 *
 * 设计说明：对齐 SolidWorks 双选测量浮窗——距离与分量均以正数展示。
 *
 * @param valueInMillimeter 长度（毫米，可有符号）。
 * @returns 文本，例如 `12.3456 mm`。
 */
export function formatMeasurePairLengthDisplay(valueInMillimeter: number): string {
    const absoluteMillimeter = toBigFromMeterScalar(valueInMillimeter).abs();
    return `${roundMeasureScalarMillimeter(absoluteMillimeter).toFixed(MEASURE_RESULT_DECIMAL_PLACES)}${MEASURE_LENGTH_UNIT_SUFFIX}`;
}

/**
 * 格式化面积测量值为带单位的展示文本（单选浮窗专用）。
 *
 * @param valueInSquareMillimeter 面积（平方毫米）。
 * @returns 文本，例如 `12.3456 mm^2`。
 */
export function formatMeasureAreaWithUnit(valueInSquareMillimeter: number): string {
    return `${formatMeasureNumber(valueInSquareMillimeter)}${MEASURE_AREA_UNIT_SUFFIX}`;
}

/**
 * 格式化坐标元组为带单位的展示文本（单选浮窗专用）。
 *
 * @param tuple 坐标元组（毫米）。
 * @returns 文本，例如 `(1.0000, 2.0000, 3.0000) mm`。
 */
export function formatMeasurePointTupleWithUnit(tuple: readonly [number, number, number]): string {
    return `${formatMeasurePointTuple(tuple)}${MEASURE_LENGTH_UNIT_SUFFIX}`;
}

/**
 * 点几何体坐标展示行（X / Y / Z 分行）。
 */
export interface MeasurePointAxisDisplayRow {
    /** 轴标签。 */
    label: string;
    /** 轴坐标展示值。 */
    value: string;
}

/**
 * 构建点几何体 X / Y / Z 三行坐标展示。
 *
 * @param tuple 坐标元组（毫米）。
 * @returns 三行展示数据。
 */
export function buildMeasurePointAxisDisplayRows(
    tuple: readonly [number, number, number],
): MeasurePointAxisDisplayRow[] {
    return [
        { label: 'X', value: formatMeasureLengthWithUnit(tuple[0]) },
        { label: 'Y', value: formatMeasureLengthWithUnit(tuple[1]) },
        { label: 'Z', value: formatMeasureLengthWithUnit(tuple[2]) },
    ];
}

/**
 * 对二维点列执行 Kasa 代数圆拟合，返回米制圆心与半径。
 *
 * @param planePoints 二维坐标点列（米）。
 * @returns 圆心与半径（米）；无法拟合时返回 null。
 */
export function fitCircleFromPlane2DPointsMeters(
    planePoints: ReadonlyArray<{ x: number; y: number }>,
): { centerU: number; centerV: number; radius: number } | null {
    if (planePoints.length < 3) {
        return null;
    }

    let sumXX = new Big('0');
    let sumXY = new Big('0');
    let sumYY = new Big('0');
    let sumX = new Big('0');
    let sumY = new Big('0');
    let sumRHS = new Big('0');
    let sumXRHS = new Big('0');
    let sumYRHS = new Big('0');
    const pointCount = new Big(planePoints.length.toString());

    planePoints.forEach(({ x, y }) => {
        const bigX = toBigFromMeterScalar(x);
        const bigY = toBigFromMeterScalar(y);
        const rhs = bigX.times(bigX).plus(bigY.times(bigY));
        sumXX = sumXX.plus(bigX.times(bigX));
        sumXY = sumXY.plus(bigX.times(bigY));
        sumYY = sumYY.plus(bigY.times(bigY));
        sumX = sumX.plus(bigX);
        sumY = sumY.plus(bigY);
        sumRHS = sumRHS.plus(rhs);
        sumXRHS = sumXRHS.plus(bigX.times(rhs));
        sumYRHS = sumYRHS.plus(bigY.times(rhs));
    });

    const determinant = sumXX.times(sumYY.times(pointCount).minus(sumY.times(sumY)))
        .minus(sumXY.times(sumXY.times(pointCount).minus(sumY.times(sumX))))
        .plus(sumX.times(sumXY.times(sumY).minus(sumYY.times(sumX))));

    if (determinant.abs().lte(NON_POSITIVE_METERS_EPSILON)) {
        return null;
    }

    const coefficientA = sumXRHS.times(sumYY.times(pointCount).minus(sumY.times(sumY)))
        .minus(sumYRHS.times(sumXY.times(pointCount).minus(sumY.times(sumX))))
        .plus(sumRHS.times(sumXY.times(sumY).minus(sumYY.times(sumX))))
        .div(determinant);
    const coefficientB = sumXX.times(sumYRHS.times(pointCount).minus(sumY.times(sumRHS)))
        .minus(sumXY.times(sumXRHS.times(pointCount).minus(sumX.times(sumRHS))))
        .plus(sumX.times(sumXRHS.times(sumY).minus(sumXY.times(sumRHS))))
        .div(determinant);
    const coefficientC = sumXX.times(sumYY.times(sumRHS).minus(sumY.times(sumYRHS)))
        .minus(sumXY.times(sumXY.times(sumRHS).minus(sumY.times(sumXRHS))))
        .plus(sumX.times(sumXY.times(sumYRHS).minus(sumYY.times(sumXRHS))))
        .div(determinant);

    const centerU = coefficientA.times('0.5');
    const centerV = coefficientB.times('0.5');
    const radiusSquared = coefficientC.plus(centerU.times(centerU)).plus(centerV.times(centerV));
    if (!radiusSquared.gt(NON_POSITIVE_METERS_EPSILON)) {
        return null;
    }

    return {
        centerU: centerU.toNumber(),
        centerV: centerV.toNumber(),
        radius: radiusSquared.sqrt().toNumber(),
    };
}

/**
 * 计算多个米制标量的算术平均（米）。
 *
 * @param valuesMeters 标量列表（米）。
 * @returns 平均值（米）。
 */
export function averageMeterScalars(valuesMeters: readonly number[]): number {
    if (valuesMeters.length === 0) {
        return 0;
    }
    let sum = new Big('0');
    valuesMeters.forEach((value) => {
        sum = sum.plus(toBigFromMeterScalar(value));
    });
    return sum.div(valuesMeters.length.toString()).toNumber();
}
